import { useState, useEffect, useMemo } from 'react';
import { getOneOrg, queryTotalNotesForOrg, getPatientTypeBreakdown } from '../../utils/functions';
import GetPatientsById from './OrgSelectorComponents/GetPatientsById';
import OrgDetails from './OrgSelectorComponents/OrgDetails2';
import NoteStatus from './OrgSelectorComponents/NoteStatus';
import ProcessingNotes from './OrgSelectorComponents/ProcessingNotes';
import PatientAdditions from './OrgSelectorComponents/PatientAdditions';

export default function OrgSelector({ asyncFunction }) {
  const [orgs, setOrgs] = useState(null);
  const [org, setOrg] = useState("");
  const [orgQuery, setOrgQuery] = useState("");
  const [orgSubmitted, setOrgSubmitted] = useState(false);
  const [doctors, setDoctors] = useState(null);
  const [doctorQuery, setDoctorQuery] = useState("");
  const [selectedSubOrg, setSelectedSubOrg] = useState("");
  const [copied, setCopied] = useState("");
  const [getOrgData, setGetOrgData] = useState(null);
  const [openOrgModal, setOpenOrgModal] = useState(false);
  const [patientTypes, setPatientTypes] = useState(null);

  useEffect(() => {
    if (asyncFunction) {
      (async () => {
        const fetchedOrgs = await asyncFunction();
        setOrgs(fetchedOrgs?.sort((a, b) => a.localeCompare(b)));
      })()
    }
  }, [])

  // Memoized refined orgs - only recalculates when orgQuery or orgs changes
  const refinedOrgs = useMemo(() => {
    if (!orgQuery || !orgs?.length) return null;
    return orgs.filter(el => el.toLowerCase().includes(orgQuery.toLowerCase())).sort((a, b) => a.localeCompare(b));
  }, [orgQuery, orgs]);

  // Get unique sub-organizations from doctors
  const uniqueSubOrgs = useMemo(() => {
    if (!doctors?.length) return [];
    
    const subOrgs = doctors
      .map(doctor => doctor.organization)
      .filter(subOrg => subOrg && subOrg.trim() !== '')
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b));
    
    return subOrgs;
  }, [doctors]);

  // Memoized refined doctors - filters by both name/email and sub-organization
  const refinedDoctors = useMemo(() => {
    if (!doctors?.length) return null;
    
    let filtered = doctors;

    if (selectedSubOrg) {
      filtered = filtered.filter(doctor => doctor.organization === selectedSubOrg);
    }

    if (doctorQuery) {
      filtered = filtered.filter(el =>
        el.user_name.toLowerCase().includes(doctorQuery.toLowerCase()) ||
        el.user_email.toLowerCase().includes(doctorQuery.toLowerCase())
      );
    }

    return filtered;
  }, [doctorQuery, doctors, selectedSubOrg]);

  const handleSetOrg = (e) => setOrg(e.target.value);

  const submitFormForOrg = async (e) => {
    e.preventDefault();
    setOrgSubmitted(true);
    setDoctors(await getOneOrg(org));
  }

  const resetOrgForm = () => {
    setOrgSubmitted(false);
    setDoctorQuery("");
    setSelectedSubOrg("");
    setOrg("");
    setOrgQuery("");
    setGetOrgData(null);
    setPatientTypes(null);
  }

  const clearFilters = () => {
    setDoctorQuery("");
    setSelectedSubOrg("");
  }

  const getAllTheNotes = async () => {
    setOpenOrgModal(true);
    if (getOrgData === null) {
      const allNotes = await queryTotalNotesForOrg(doctors.map(el => ({ 
        userName: el.user_name.split(" ").join(""), 
        userEmail: el.user_email 
      })));
      setGetOrgData(allNotes);
    }
  }

  const copyToClipboard = async (e) => {
    navigator.clipboard.writeText(e.target.innerText);
    setCopied(`Successfully copied: ${e.target.innerText}`);
    setTimeout(() => setCopied(""), 1000);
  }

  return (
    <>
      {!orgSubmitted && (
        <form className="flex gap-2 w-full mb-4 justify-between" onSubmit={submitFormForOrg}>
          <input 
            className="w-[30%] rounded-md" 
            type='text' 
            value={orgQuery} 
            onChange={(e) => setOrgQuery(e.target.value)} 
            placeholder="Search" 
          />
          <select className="w-[30%] rounded-md" placeholder='Org' onChange={handleSetOrg} value={org}>
            <option value="" disabled>Org</option>
            {refinedOrgs ?
              refinedOrgs.map((el, i) => (
                <option key={i} value={el}>{el}</option>
              )) : orgs && orgs.map((el, i) => (
                <option key={i} value={el}>{el}</option>
              ))
            }
          </select>
          <button 
            disabled={org === ""} 
            className="w-[30%] rounded-md border-sage-green border-[1px] text-sage-green hover:bg-sage-green hover:text-white font-semibold" 
            type="submit"
          >
            Submit
          </button>
        </form>
      )}

      {orgSubmitted && (
        <div className="fixed inset-0 bg-white z-[30] flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={resetOrgForm}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Back to organization selection"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div>
                  <h1 className="text-xl font-bold text-ktx-purple">{org}</h1>
                  <p className="text-sm text-gray-600">
                    {doctors?.length || 0} doctors • {refinedDoctors?.length || 0} shown
                  </p>
                </div>
              </div>
              
              <button 
                onClick={getAllTheNotes} 
                className="rounded-md border-sage-green border-[1px] text-sage-green hover:bg-sage-green hover:text-white py-2 px-4 font-semibold"
              >
                Org Notes
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <input 
                  className="w-full rounded-md" 
                  type='text' 
                  value={doctorQuery} 
                  onChange={(e) => setDoctorQuery(e.target.value)} 
                  placeholder="Doctor Name / Email" 
                />
              </div>
              
              {uniqueSubOrgs.length > 0 && (
                <div className="flex-1">
                  <select 
                    className="w-full rounded-md" 
                    value={selectedSubOrg} 
                    onChange={(e) => setSelectedSubOrg(e.target.value)}
                  >
                    <option value="">All Departments</option>
                    {uniqueSubOrgs.map((subOrg, i) => (
                      <option key={i} value={subOrg}>{subOrg}</option>
                    ))}
                  </select>
                </div>
              )}

              {(doctorQuery || selectedSubOrg) && (
                <button 
                  onClick={clearFilters}
                  className="px-3 py-2 rounded-md border-gray-300 border-[1px] text-gray-600 hover:bg-gray-100 transition-colors"
                  title="Clear all filters"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Filter status */}
            {(doctorQuery || selectedSubOrg) && (
              <div className="mt-2 text-sm text-gray-600">
                Showing {refinedDoctors?.length || 0} of {doctors?.length || 0} doctors
                {selectedSubOrg && <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Dept: {selectedSubOrg}</span>}
                {doctorQuery && <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Search: "{doctorQuery}"</span>}
              </div>
            )}
          </div>

          {/* Table Container - This needs to flex and overflow */}
          <div className="flex-1 min-h-0 p-4">
            <div className="h-full border border-gray-200 rounded-lg overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50 sticky top-0 z-[35]">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Email</th>
                    {uniqueSubOrgs.length > 0 && <th className="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Department</th>}
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Int</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Status</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Patients</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Sign-Up | First Note</th>
                    <th className="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                      <span 
                        className="cursor-help border-b border-dotted border-gray-400 hover:border-gray-600 transition-colors z-[40]"
                        title="Total Patients | Added Patients"
                      >
                        TP | AP
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {refinedDoctors !== null ? refinedDoctors.map((row, index) => (
                    <tr className={`hover:bg-gray-50 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`} key={row.user_id}>
                      <td className="py-3 px-4 border-b border-gray-100">{row.user_name}</td>
                      <td className="py-3 px-4 border-b border-gray-100">
                        <button onClick={copyToClipboard} className="text-blue-600 hover:text-blue-800 transition-colors">
                          {row.user_email}
                        </button>
                      </td>
                      {uniqueSubOrgs.length > 0 && (
                        <td className="py-3 px-4 border-b border-gray-100 text-sm text-gray-600">
                          {row.organization || '-'}
                        </td>
                      )}
                      <td className="text-center py-3 px-4 border-b border-gray-100">
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                          row.access_token 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {row.access_token ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className="text-center py-3 px-4 border-b border-gray-100">
                        <NoteStatus doctor_id={row.user_id} access_token={row.access_token} doctor_name={row.user_name} />
                      </td>
                      <td className="text-center py-3 px-4 border-b border-gray-100">
                        <GetPatientsById doctor_id={row.user_id} doctor_name={row.user_name} />
                      </td>
                      <td className="text-center py-3 px-4 border-b border-gray-100">
                        <ProcessingNotes doctor_id={row.user_id} />
                      </td>
                      <td className="text-center py-3 px-4 border-b border-gray-100">
                        <PatientAdditions doctor_id={row.user_id} />
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={uniqueSubOrgs.length > 0 ? "8" : "7"} className="text-center py-12">
                        <div className="flex flex-col items-center gap-3">
                          <span className="w-8 h-8 border-2 border-dashed rounded-full animate-spin border-ktx-purple inline-block"></span>
                          <span className="text-gray-500">Loading doctors...</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Modals */}
          {openOrgModal && (
            <OrgDetails 
              getOrgData={getOrgData} 
              org={org} 
              setOpenOrgModal={setOpenOrgModal} 
              doctorsArr={doctors} 
            />
          )}

          {/* Copy notification */}
          {copied && (
            <div className="fixed bottom-6 right-6 max-w-[300px] bg-white shadow-lg rounded-md p-3 z-[60] border">
              {copied}
            </div>
          )}
        </div>
      )}
    </>
  );
};